import React from 'react';
import { theme } from 'antd';
import { type ChartDataset } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';

import { numberFormatter } from '~library/numbers';
import { getXAxisConfig, getKPIChartDatasetOptions } from './Chart';
import { getKPIComparison } from './components/KPIComparison';
import { type WidgetLoadError } from '~features/empty-states';
import KPICard from './components/KPICard';
import KPICardContent from './components/KPICardContent';
import { useIsPrinting } from '~library/print';

function KPIErrorRateContents({ rawData }: { rawData: AggregatedMetrics.ErrorRateSummaryResult }) {
  const { token } = theme.useToken();
  const { isPrinting } = useIsPrinting();

  const comparison = React.useMemo(
    () => getKPIComparison(rawData.vCurrent, rawData.vPrevious),
    [rawData.vCurrent, rawData.vPrevious]
  );

  const dataset = React.useMemo(() => {
    const data = rawData.data.reduce<{
      labels: Date[];
      datasets: ChartDataset<'line'>[];
    }>(
      (acc, item) => {
        acc.labels.push(new Date(item.time));
        acc.datasets[0].data.push(+(item.rate * 100).toFixed(2));
        return acc;
      },
      { labels: [], datasets: [{ data: [] }] }
    );
    let lineColor = token.colorText;
    if (comparison.kind === 'delta') {
      if (comparison.percentage < 0) lineColor = token.colorSuccess;
      else if (comparison.percentage > 0) lineColor = token.colorError;
    }

    Object.assign(data.datasets[0], getKPIChartDatasetOptions(lineColor));

    return data;
  }, [comparison, rawData, token.colorError, token.colorSuccess, token.colorText]);

  const percentageColor =
    comparison.kind === 'delta' && comparison.percentage < 0 ? 'success' : 'danger';

  return (
    <KPICardContent
      comparison={comparison}
      percentageColor={percentageColor}
      mainValue={`${numberFormatter.format(rawData.vCurrent * 100 || 0)}%`}
    >
      <Line
        data={dataset}
        options={{
          animation: isPrinting ? false : undefined,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ...getXAxisConfig(rawData.granularity), display: false },
            y: { display: false },
          },
          plugins: {
            tooltip: {
              enabled: false,
            },
          },
        }}
      />
    </KPICardContent>
  );
}

export const TOOLTIP_MESSAGE =
  'The percentage of requests that result in errors, rather than successful caches. Red error rates could indicate issues with page rendering, server responses, or configuration.';

export interface KPIErrorRateProps {
  isLoading: boolean;
  error?: WidgetLoadError;
  rawData?: AggregatedMetrics.ErrorRateSummaryResult;
}

function KPIErrorRate({ isLoading, rawData, error }: KPIErrorRateProps) {
  const link = React.useMemo(() => {
    const urlParameters = new URLSearchParams(window.location.search);
    urlParameters.set('status_code_low', '500');
    urlParameters.set('status_code_high', '599');
    return `/cdn-analytics?${urlParameters.toString()}#table`;
  }, [window.location.search]);

  return (
    <KPICard title="Error Rate" titleTooltip={TOOLTIP_MESSAGE} isLoading={isLoading} error={error} link={link}>
      {rawData && <KPIErrorRateContents rawData={rawData} />}
    </KPICard>
  );
}

export default KPIErrorRate;
